import React, { useState } from "react";
import { Colors } from "../../Constants/colors";
import { TextButton, TextBtnType, TextBtnSize } from "../Button/TextButton";
import { CertificateCardProps } from "./types";
import {
  ALIGN_ITEMS,
  DISPLAY,
  FLEX_DIRECTION,
  FONT_SIZE,
  FONT_WEIGHT,
  JUSTIFY_CONTENT,
  SPACING,
} from "../../Constants/Enums/styles.enum";
import { DownloadIcon } from "../Icon/DownloadIcon";
import Pills from "../pills";
import { downloadPdfFromBuffer } from "../../utils/pdf.utils";
import { BufferView } from "../BufferView/BufferView";

export const CertificateCard: React.FC<CertificateCardProps> = ({
  certificate,
  index,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleDownloadCertificate = (): void => {
    downloadPdfFromBuffer(certificate?.buffer, certificate?.filename);
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: "100%",
        maxWidth: "360px",
        display: DISPLAY.FLEX,
        flexDirection: FLEX_DIRECTION.COLUMN,
        borderRadius: SPACING.S12,
        boxShadow: isHovered
          ? "rgba(99, 99, 99, 0.3) 0px 4px 16px 0px"
          : "rgba(99, 99, 99, 0.15) 0px 2px 10px 0px",
        backgroundColor: Colors.White,
        transition: "all 0.3s ease",
        transform: isHovered ? "translateY(-6px)" : "translateY(0)",
        border: `1px solid ${isHovered ? Colors.LightGrey : "#e8e8e8"}`,
        overflow: "hidden",
      }}
    >
      {/* Header with Icon and Badge */}
      <div
        style={{
          padding: SPACING.S16,
          paddingBottom: SPACING.S12,
          background: `linear-gradient(135deg, ${Colors.LIGHT_TANGERINE_ORANGE}15 0%, ${Colors.White} 100%)`,
          borderBottom: `1px solid ${Colors.LightGrey}20`,
        }}
      >
        <div
          style={{
            display: DISPLAY.FLEX,
            alignItems: ALIGN_ITEMS.START,
            justifyContent: JUSTIFY_CONTENT.SPACE_BETWEEN,
            gap: SPACING.S12,
          }}
        >
          <div
            style={{
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
              gap: SPACING.S12,
              flex: 1,
            }}
          >
            {/* Certificate Icon */}
            <div
              style={{
                width: "44px",
                height: "44px",
                minWidth: "44px",
                borderRadius: SPACING.S8,
                backgroundColor: Colors.LIGHT_TANGERINE_ORANGE,
                display: DISPLAY.FLEX,
                alignItems: ALIGN_ITEMS.CENTER,
                justifyContent: JUSTIFY_CONTENT.CENTER,
                boxShadow: "rgba(255, 138, 76, 0.25) 0px 4px 12px",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                  stroke={Colors.TangerineOrange}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 2V8H20M16 13H8M16 17H8M10 9H8"
                  stroke={Colors.TangerineOrange}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Title and Badge */}
            <div
              style={{
                display: DISPLAY.FLEX,
                flexDirection: FLEX_DIRECTION.COLUMN,
                gap: SPACING.S6,
                flex: 1,
                minWidth: 0,
              }}
            >
              <h3
                style={{
                  fontSize: FONT_SIZE.F16,
                  fontWeight: FONT_WEIGHT.SEMI_BOLD,
                  color: Colors.Black,
                  margin: 0,
                  wordBreak: "break-word",
                  lineHeight: "1.3",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {certificate.certificateName}
              </h3>
              <Pills title={`Certificate #${index + 1}`} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Preview */}
      <div
        style={{
          padding: SPACING.S12,
          paddingTop: SPACING.S14,
        }}
      >
        <BufferView
          buffer={certificate.buffer}
          fileName={certificate.filename}
        />
      </div>

      {/* Footer with Actions */}
      <div
        style={{
          padding: SPACING.S16,
          paddingTop: SPACING.S12,
          backgroundColor: `${Colors.VeryLightGrey}30`,
          borderTop: `1px solid ${Colors.LightGrey}20`,
          display: DISPLAY.FLEX,
          alignItems: ALIGN_ITEMS.CENTER,
          justifyContent: JUSTIFY_CONTENT.SPACE_BETWEEN,
          gap: SPACING.S12,
        }}
      >
        {/* Certificate Info */}
        <div
          style={{
            display: DISPLAY.FLEX,
            flexDirection: FLEX_DIRECTION.COLUMN,
            gap: SPACING.S2,
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZE.F12,
              color: Colors.Midgrey,
              fontWeight: FONT_WEIGHT.MEDIUM,
            }}
          >
            Certificate Document
          </span>
          <span
            style={{
              fontSize: FONT_SIZE.F10,
              color: Colors.Grey,
            }}
          >
            PDF Format
          </span>
        </div>

        {/* Download Button */}
        <TextButton
          type={TextBtnType.primary}
          title="Download"
          size={TextBtnSize.sm}
          onClick={handleDownloadCertificate}
          icon={<DownloadIcon />}
        />
      </div>
    </div>
  );
};
